import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Chip, Grid, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { PLATFORM_FEATURES } from './platformFeatures';

const PlatformFeatureGrid: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5 }}>
        Platform Features
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
        Everything EncycGames offers — structured rubrics, learning, rankings, contests, and partnerships — in one place.
      </Typography>

      <Grid container spacing={2.5}>
        {PLATFORM_FEATURES.map((feature) => (
          <Grid item xs={12} sm={6} lg={3} key={feature.id}>
            <Box
              onClick={() => navigate(feature.path)}
              sx={{
                position: 'relative',
                height: '100%',
                minHeight: 280,
                borderRadius: 3,
                overflow: 'hidden',
                cursor: 'pointer',
                background: feature.gradient,
                border: '1px solid rgba(255,255,255,0.12)',
                boxShadow: `0 12px 40px ${feature.glow}`,
                transition: 'transform 0.22s ease, box-shadow 0.22s ease',
                '&:hover': {
                  transform: 'translateY(-6px)',
                  boxShadow: `0 20px 48px ${feature.glow}`,
                },
              }}
            >
              {feature.image && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `linear-gradient(160deg, rgba(15,23,42,0.92) 0%, rgba(15,23,42,0.55) 45%, rgba(124,45,18,0.75) 100%), url(${feature.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
              )}
              <Box
                sx={{
                  position: 'relative',
                  p: 2.5,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    mb: 1.5,
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                  }}
                >
                  {feature.icon}
                </Box>

                <Typography
                  variant="overline"
                  sx={{ color: feature.accent, fontWeight: 800, letterSpacing: 1.2, lineHeight: 1.2 }}
                >
                  {feature.subtitle}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 900, color: '#fff', mb: 0.75, lineHeight: 1.2 }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.88)', mb: 1.5, flexGrow: 1, lineHeight: 1.5 }}>
                  {feature.description}
                </Typography>

                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 2 }}>
                  {feature.chips.map((chip) => (
                    <Chip
                      key={chip}
                      label={chip}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.12)',
                        color: '#f8fafc',
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        border: '1px solid rgba(255,255,255,0.15)',
                      }}
                    />
                  ))}
                </Stack>

                <Button
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(feature.path);
                  }}
                  sx={{
                    alignSelf: 'flex-start',
                    color: '#fff',
                    fontWeight: 800,
                    textTransform: 'none',
                    bgcolor: 'rgba(255,255,255,0.12)',
                    border: '1px solid rgba(255,255,255,0.25)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
                  }}
                >
                  {feature.cta}
                </Button>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PlatformFeatureGrid;
